import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { AlertColor } from '@mui/material/Alert';
import { setApiErrorHandler } from '../api/client';

type ShowSnackbar = (message: string, severity?: AlertColor) => void;

const SnackbarContext = createContext<ShowSnackbar | null>(null);

export function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'error',
  });

  const show = useCallback<ShowSnackbar>((message, severity = 'error') => {
    setState({ open: true, message, severity });
  }, []);

  const handleClose = useCallback(() => setState(s => ({ ...s, open: false })), []);

  useEffect(() => {
    setApiErrorHandler(show);
    return () => setApiErrorHandler(null);
  }, [show]);

  return (
    <SnackbarContext.Provider value={show}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled" sx={{ width: '100%' }}>
          {state.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): ShowSnackbar {
  const fn = useContext(SnackbarContext);
  if (!fn) throw new Error('useSnackbar must be used within SnackbarProvider');
  return fn;
}
